import { Space, Tag } from "antd";
import Broker from "components/Message/Broker";
import IntlMessages from "components/Message/IntlMessages";
import { ReceiveMethod, SecurityType, OfferType, UnitDisplay, ErrorType } from "const";
import { concatAddress, isEmpty, isNotEmpty } from "./common-util";
import { calculate, formatNumber, workAroundNumber } from "./math";
import indexOf from "lodash/indexOf";
import find from "lodash/find";
import compact from "lodash/compact";
import ErrorMessageConst from "components/Message/ErrorMessageConst";
import { Fragment } from "react";
import Income from "components/Message/Income";
import { isMainTitleAndGenderMatched, isTitleOther } from "./customer-util";
import { isUnLimit } from "./redeem";
import { mapAccountsToSelectOptions } from "./misc/account-util";
import { USER_INPUT_EMAIL_REGEX } from "./form";

export const isUnitDisplay = (unitDisplay) => unitDisplay?.toLowerCase() === UnitDisplay.UNIT;
export const isBahtDisplay = (unitDisplay) =>
  unitDisplay?.toLowerCase() === UnitDisplay.BAHT || unitDisplay?.toLowerCase() === "thb";
export const checkSecurityType = (productOrSecurityType, comparedType) => {
  return typeof productOrSecurityType === "object"
    ? productOrSecurityType.securityType === comparedType
    : productOrSecurityType === comparedType;
};
export const isSecurityTypeUNT = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.UNT);
export const isSecurityTypeDBT = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.DBT);
export const isSecurityTypeSTO = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.STO);
export const isSecurityTypeCOM = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.COM);
export const isSecurityTypeSB = (productOrSecurityType) => checkSecurityType(productOrSecurityType, SecurityType.SB);

export const checkOfferType = (productOrOfferType, comparedType) => {
  return typeof productOrOfferType === "object"
    ? productOrOfferType.offerType === comparedType
    : productOrOfferType === comparedType;
};
export const isPO = (productOrOfferType) => checkOfferType(productOrOfferType, OfferType.PO);
export const isRO = (productOrOfferType) => checkOfferType(productOrOfferType, OfferType.RO);

export const getUnitType = (productOrSecurityType, requestReturnId) => {
  const messageId = isSecurityTypeSTO(productOrSecurityType) ? "global.unit.common-stock" : "global.unit.default";
  return requestReturnId ? messageId : <IntlMessages id={messageId} />;
};

export const unitToBaht = (unit, sellPriceUnit) => {
  unit = workAroundNumber(unit);
  sellPriceUnit = workAroundNumber(sellPriceUnit);
  return calculate(`${unit} * ${sellPriceUnit}`);
};

export const bahtToUnit = (baht, sellPriceUnit) => {
  baht = workAroundNumber(baht);
  sellPriceUnit = workAroundNumber(sellPriceUnit);
  return calculate(`${baht} / ${sellPriceUnit}`, 8);
};

export const UnitValidator = {
  validateMinAndMultiple: (unit, productDetail) => {
    const vMultiple = UnitValidator.validMultiple(unit, productDetail);
    const vMinimum = UnitValidator.validMinimum(unit, productDetail);
    return vMultiple && vMinimum;
  },
  validMultiple: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const multiple = workAroundNumber(productDetail.sellMultipleUnit);
    return unit > 0 && unit % multiple === 0;
  },
  validMinimum: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const min = workAroundNumber(productDetail.sellMinUnit);
    return unit > 0 && unit >= min;
  },
  validMaxUnit: (unit, productDetail) => {
    unit = workAroundNumber(unit);
    const maxUnit = productDetail.sellMaxUnit ? workAroundNumber(productDetail.sellMaxUnit) : Infinity;
    return unit <= maxUnit;
  },
  validRemainUnit: (unit, _, unitHolder) => {
    if (!unitHolder) return false;
    if (isUnLimit(unitHolder)) return true;
    unit = workAroundNumber(unit);
    const remainUnit = workAroundNumber(unitHolder.remainUnit);
    return unit <= remainUnit;
  },
};

export const BahtValidator = {
  validateMinAndMultiple: (baht, productDetail) => {
    const vMultiple = BahtValidator.validMultiple(baht, productDetail);
    const vMinimum = BahtValidator.validMinimum(baht, productDetail);
    return vMultiple && vMinimum;
  },
  validMultiple: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.sellPriceUnit);
    return UnitValidator.validMultiple(unit, productDetail);
  },
  validMinimum: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.sellPriceUnit);
    return UnitValidator.validMinimum(unit, productDetail);
  },
  validMaxUnit: (baht, productDetail) => {
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.sellPriceUnit);
    return UnitValidator.validMaxUnit(unit, productDetail);
  },
  validRemainUnit: (baht, productDetail, unitHolder) => {
    if (!unitHolder) return false;
    if (isUnLimit(unitHolder)) return true;
    baht = workAroundNumber(baht);
    const unit = bahtToUnit(baht, productDetail.sellPriceUnit);
    return UnitValidator.validRemainUnit(unit, null, unitHolder);
  },
};

export const validateBalancePaymentAccount = (totalPayment, account, paymentMethod = "payNow") => {
  if (paymentMethod === "payNow") {
    if (!account) return false;

    const valid = calculate(`${account.availBalAmt} - ${totalPayment}`) >= 0;
    return valid;
  }
  return true;
};

export const validateAddress = (data) => {
  return (
    data.mailingAddress &&
    data.mailingProvince &&
    data.mailingDistrict &&
    data.mailingSubDistrict &&
    data.mailingZipCode &&
    data.mailingZipCode.length === 5
  );
};
export const validateIncomeSource = (data) => {
  return !!data.incomeSource;
};
export const validateEmail = (data) => !!(data.email && USER_INPUT_EMAIL_REGEX.test(data.email));
export const validateBroker = (data, productDetail) => {
  if (productDetail?.isSecurityOffshoreNote) {
    return true; // always valid for security offshore note because user can not edit broker
  }
  return !!data.receiveSecurityCompanyCode && !!data.receiveAccountNumber;
};
export const validateReceiveMethod = (data, productDetail) => {
  let receiveMethodValid;
  switch (data.receiveMethod) {
    case ReceiveMethod.POSTAL:
      receiveMethodValid = validateAddress(data);
      break;
    case ReceiveMethod.BROKER:
      receiveMethodValid = validateBroker(data, productDetail);
      break;
    case ReceiveMethod.TSD:
      receiveMethodValid = true;
      break;
    default:
      receiveMethodValid = false;
  }

  return receiveMethodValid;
};

export function renderReceiveMethodLabel(data, productDetail) {
  const { receiveMethod, receiveMailing, receiveMethodRemark } = data;
  return (
    <>
      {ReceiveMethod.POSTAL === receiveMethod && (
        <>
          <div className="mb-1">
            <Tag className="tag-main me-0">
              {receiveMailing ? (
                <IntlMessages id="receive.methods.choice.postal-address-1.2" />
              ) : (
                <IntlMessages id="receive.methods.choice.postal-address" />
              )}
            </Tag>
          </div>
          <span>{receiveMailing ? receiveMethodRemark : concatAddress(data)}</span>
        </>
      )}
      {ReceiveMethod.BROKER === receiveMethod && (
        <>
          <div className="mb-1">
            <Tag className="tag-main me-0">
              <IntlMessages id="receive.methods.securities-company" />
            </Tag>
          </div>
          {data.receiveSecurityCompanyCode && (
            <Space size={2}>
              <span>{data.receiveSecurityCompanyCode}</span>
              <span>:</span>
              <Broker code={data.receiveSecurityCompanyCode} />
            </Space>
          )}
          {!(productDetail?.isSecurityOffshoreNote && !data.receiveAccountNumber) && (
            <div>
              <span className="me-1">
                <IntlMessages id="global.company.no" />
              </span>
              <span>{data.receiveAccountNumber}</span>
            </div>
          )}
        </>
      )}
      {ReceiveMethod.TSD === receiveMethod && (
        <>
          <div className="mb-1">
            <Tag className="tag-main me-0">
              <IntlMessages id="receive.methods.choice.securities-depository" />
            </Tag>
          </div>
          <div>
            <span className="me-1">
              <IntlMessages id="receive.methods.choice.securities-depository.desc" />
            </span>
          </div>
        </>
      )}
    </>
  );
}

export function getAccountByCCY(accounts, ccy = "THB", fieldCY = "currCd") {
  if (!accounts) return [];
  return accounts.filter((account) => account[fieldCY].toUpperCase() === ccy.toUpperCase());
}

export function getAccountOptionByCCY(accounts, ccy, fieldCY) {
  return mapAccountsToSelectOptions(getAccountByCCY(accounts, ccy, fieldCY));
}

export function getAccountByAccountNo(accNo, accounts) {
  if (!accNo) return;
  return accounts?.find((a) => a.accountNo === accNo);
}

export function getUnitHoldersByAcId(acId, unitHolders) {
  if (!acId) return;
  return unitHolders?.find((u) => u.acId === acId);
}

export function displayIncomeSource(incomeSource, otherDesc) {
  // prepare
  incomeSource = (Array.isArray(incomeSource) ? incomeSource : incomeSource?.trim().split("|")) ?? [];
  // move other to last
  const othIndex = indexOf(incomeSource, "OTH");
  const hasOtherChoice = othIndex !== -1;
  if (hasOtherChoice) {
    incomeSource.splice(othIndex, 1);
    incomeSource.push("OTH");
  }
  // render
  return (
    <>
      {incomeSource
        .filter((v) => v)
        .map((source, i, arr) => (
          <Fragment key={source}>
            <Income code={source} />
            {i !== arr.length - 1 && <span>, </span>}
          </Fragment>
        ))}
      {hasOtherChoice && otherDesc && ` | ${otherDesc}`}
    </>
  );
}

export const ruleUnits = (unitDisplay, productDetail, getFieldsValue, intl) => {
  const noPayment = !!productDetail.isDebtIntoCapitalProduct;

  const validateMinAndMultiple = (Validator) => ({
    validator: (_, amount) => {
      const formValues = getFieldsValue(["unitHolderInterest"]);
      const useInterestToConvert = !!formValues.unitHolderInterest;
      if (
        (workAroundNumber(amount) === 0 && noPayment && useInterestToConvert) ||
        Validator.validateMinAndMultiple(amount, productDetail)
      ) {
        return Promise.resolve();
      } else {
        return Promise.reject();
      }
    },
    message: isPO(productDetail) ? (
      <ErrorMessageConst code={ErrorType.INVALID_UNIT_PO} />
    ) : (
      <ErrorMessageConst code={ErrorType.INVALID_UNIT_RO} />
    ),
  });

  const validateMaxUnit = (Validator) =>
    (productDetail.sellMaxUnit || productDetail.useLombard) && {
      validator: (_, amount) => {
        const formValues = getFieldsValue(["unitHolderInterest"]);
        const useInterestToConvert = !!formValues.unitHolderInterest;
        if (
          (workAroundNumber(amount) === 0 && noPayment && useInterestToConvert) ||
          Validator.validMaxUnit(amount, productDetail)
        ) {
          return Promise.resolve();
        } else {
          return Promise.reject();
        }
      },
      message: (
        <>
          <IntlMessages
            id="validate.unit-holder.NB0003"
            values={{
              unit: formatNumber(productDetail.sellMaxUnit),
              unitDisplay: getUnitType(productDetail),
              baht: formatNumber(unitToBaht(productDetail.sellMaxUnit, productDetail.sellPriceUnit)),
            }}
          />
        </>
      ),
    };

  const validateRemainUnit = (Validator) => {
    return {
      validator: (_, amount) => {
        const { acId, unitHolderInterest } = getFieldsValue(["acId", "unitHolderInterest"]);

        if (!acId || !productDetail.unitHolders) {
          return Promise.resolve();
        }

        const unitHolder = find(productDetail.unitHolders, { acId });
        const useInterestToConvert = !!unitHolderInterest;

        if (
          (workAroundNumber(amount) === 0 && noPayment && useInterestToConvert) ||
          Validator.validRemainUnit(amount, productDetail, unitHolder)
        ) {
          return Promise.resolve();
        } else {
          const errorMessage = intl.formatMessage(
            { id: "validate.unit-holder.remain-unit" },
            {
              unit: formatNumber(unitHolder.remainUnit),
              unitDisplay: intl.formatMessage({ id: getUnitType(productDetail, true) }),
              baht: formatNumber(unitToBaht(unitHolder.remainUnit, productDetail.sellPriceUnit), 2),
            },
          );
          return Promise.reject(new Error(errorMessage));
        }
      },
    };
  };

  const Validator = isBahtDisplay(unitDisplay) ? BahtValidator : UnitValidator;
  return compact([validateMinAndMultiple(Validator), validateMaxUnit(Validator), validateRemainUnit(Validator)]);
};

export const ruleUnitsComplex = (getFieldsValue) => {
  const validateAmount = () => {
    return {
      validator: () => {
        const { unit } = getFieldsValue(["unit"]);
        return unit > 0 ? Promise.resolve() : Promise.reject();
      },
      message: (
        <>
          <div>
            <IntlMessages id="validate.amount.required" />
          </div>
        </>
      ),
    };
  };

  return compact([validateAmount()]);
};

export const ruleBahtComplex = (getFieldsValue) => {
  const validateAmount = () => {
    return {
      validator: () => {
        const { baht } = getFieldsValue(["baht"]);
        return baht > 0 ? Promise.resolve() : Promise.reject();
      },
      message: (
        <>
          <div>
            <IntlMessages id="validate.amount.product-complex" />
          </div>
        </>
      ),
    };
  };

  return compact([validateAmount()]);
};

export const calculatePremiumDiscountTotalPrice = (unit, pricePerUnit, parPerUnit) => {
  return {
    paymentAmount: calculate(`${unit} * ${pricePerUnit}`, 2),
    faceAmount: calculate(`${unit} * ${parPerUnit}`, 2),
  };
};

export const isTitleAndGenderValid = (subscriptionDetail, titleOthers) => {
  let isValid = true;

  if (subscriptionDetail.isGenderAndTitleInvalid) {
    if (isTitleOther(subscriptionDetail.title)) {
      const titleOtherOption = find(titleOthers, { titleDesc: subscriptionDetail.titleOther });
      isValid =
        titleOtherOption &&
        (isEmpty(titleOtherOption.sexCode) || titleOtherOption.sexCode === subscriptionDetail.gender);
    } else {
      isValid = isMainTitleAndGenderMatched(subscriptionDetail.gender, subscriptionDetail.title);
    }
  }

  return isValid;
};

export const SubscriptionValidator = {
  isSelectUnitPageValid: (subscriptionDetail, productDetail, unitDisplay) => {
    const isUnitDisplaying = unitDisplay === UnitDisplay.UNIT;
    const amount = isUnitDisplaying ? subscriptionDetail.unit : subscriptionDetail.baht;
    const Validator = isUnitDisplaying ? UnitValidator : BahtValidator;

    const noPayment = !!productDetail.isDebtIntoCapitalProduct;
    const useInterestToConvert = !!subscriptionDetail.unitHolderInterest;
    const skipValidateUnit = workAroundNumber(amount) === 0 && noPayment && useInterestToConvert;

    const acceptDisc04 = subscriptionDetail.acceptDisc04;
    const amountIsValid = skipValidateUnit || Validator.validateMinAndMultiple(amount, productDetail);
    const maxUnitIsValid = skipValidateUnit || Validator.validMaxUnit(amount, productDetail);
    const remainUnitValid =
      skipValidateUnit ||
      !productDetail.unitHolders || // only RO Tier 1 have an unitHolders
      Validator.validRemainUnit(amount, productDetail, subscriptionDetail.unitHolder);

    return acceptDisc04 && amountIsValid && maxUnitIsValid && remainUnitValid;
  },
  isSelectAccountPageValid: (subscriptionDetail, productDetail, accounts) => {
    const { accountNo, rfdAccountNo, unit } = subscriptionDetail;
    const noPayment = !!productDetail.isDebtIntoCapitalProduct;
    const paymentAccountValid = noPayment || !!accountNo;
    const receiveAccountValid = !!rfdAccountNo;
    const paymentAccountBalanceValid =
      noPayment ||
      (accountNo &&
        accounts &&
        accounts.length > 0 &&
        (() => {
          const paymentAccount = getAccountByAccountNo(accountNo, accounts);
          const baht = unitToBaht(unit, productDetail.sellPriceUnit);
          return validateBalancePaymentAccount(baht, paymentAccount, productDetail.sellPaymentMethod);
        })());
    return paymentAccountValid && receiveAccountValid && paymentAccountBalanceValid;
  },
  isConfirmationPageValid: (subscriptionDetail, titleOthers, productDetail) => {
    const incomeSourceValid = validateIncomeSource(subscriptionDetail);
    const emailValid = validateEmail(subscriptionDetail);
    const receiveMethodValid = validateReceiveMethod(subscriptionDetail, productDetail);
    const titleAndGenderValid = isTitleAndGenderValid(subscriptionDetail, titleOthers);
    return incomeSourceValid && emailValid && receiveMethodValid && titleAndGenderValid;
  },
};

export const ComplexProductValidator = {
  isSelectUnitPageValid: (subscriptionDetail, productDetail) => {
    const acceptDisc04 = subscriptionDetail.acceptDisc04;
    const isAmount = subscriptionDetail.baht > 0;

    const mncQuotaId =
      productDetail.mncQuotaList?.length > 1 ? subscriptionDetail.mncQuotaId : productDetail.mncQuotaList[0].id;
    const isNotPassCourse =
      isNotEmpty(mncQuotaId) && !productDetail.mncQuotaList.find(({ id }) => id === mncQuotaId)?.passCourse;

    return acceptDisc04 && isAmount && !isNotPassCourse;
  },
  isSelectAccountPageValid: (subscriptionDetail, productDetail, accounts, ccyList) => {
    const { accountNo, rfdAccountNoCcy1, rfdAccountNoCcy2, unit } = subscriptionDetail;
    const paymentAccountValid = !!accountNo;
    const paymentAccountBalanceValid =
      accountNo &&
      accounts &&
      accounts.length > 0 &&
      (() => {
        const paymentAccount = getAccountByAccountNo(accountNo, accounts);
        const baht = unitToBaht(unit, productDetail.sellPriceUnit);
        return validateBalancePaymentAccount(baht, paymentAccount, productDetail.sellPaymentMethod);
      })();

    //receive Account CCY
    const paymentReceiveCcy = ccyList.map((_, index) => {
      return index === 0 ? !!rfdAccountNoCcy1 : !!rfdAccountNoCcy2;
    });
    const paymentReceiveValid = paymentReceiveCcy.every((index) => index === true);

    return paymentAccountValid && paymentAccountBalanceValid && paymentReceiveValid;
  },
  isConfirmationPageValid: (subscriptionDetail, titleOthers) => {
    const incomeSourceValid = validateIncomeSource(subscriptionDetail);
    const emailValid = validateEmail(subscriptionDetail);
    const receiveMethodValid = validateReceiveMethod(subscriptionDetail);
    const titleAndGenderValid = isTitleAndGenderValid(subscriptionDetail, titleOthers);
    return incomeSourceValid && emailValid && receiveMethodValid && titleAndGenderValid;
  },
};
